import React, { useEffect, useMemo, useState } from 'react';
import { ref, set } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { auth, database } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { watchPath } from '../../services/crmService';
import { ui } from './crmUi';

// Inbox: replies from clients to the Resi texting number.
// Each conversation is one client phone. Replies here go out from the same Resi number as plain text (no name or prefix added) and are logged.
const when = (iso) => { try { return new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return ''; } };

export default function InboxPage() {
  const { user } = useAuth();
  const uid = user?.uid;
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => (uid ? watchPath(`crm/inboxThreads/${uid}`, (l) => setThreads(l.sort((a, b) => String(b.lastAt || '').localeCompare(String(a.lastAt || ''))))) : undefined), [uid]);
  useEffect(() => {
    if (!uid || !open) { setMsgs([]); return undefined; }
    return watchPath(`crm/inboxMsgs/${uid}/${open}`, (l) => setMsgs(l.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')))));
  }, [uid, open]);
  const thread = useMemo(() => threads.find((t) => t.id === open), [threads, open]);
  // Opening a conversation marks it read.
  useEffect(() => { if (uid && thread && thread.unread) set(ref(database, `crm/inboxThreads/${uid}/${thread.id}/unread`), 0).catch(() => {}); }, [uid, thread]);

  const send = async () => {
    if (!draft.trim() || !open) return;
    setBusy(true); setNote('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch('/api/inbox-reply', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify({ threadKey: open, content: draft.trim() }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setNote(data.error || `Could not send (error ${res.status}).`); return; }
      if (data.testMode) setNote('Test mode: your reply was saved but no text went out.');
      setDraft('');
    } catch (e) { setNote(e.message || 'Could not send.'); } finally { setBusy(false); }
  };

  const unreadTotal = threads.reduce((n, t) => n + (Number(t.unread) || 0), 0);
  return (
    <div style={ui.page}>
      <h1 style={ui.h1}>Inbox</h1>
      <p style={ui.sub}>Texts your clients send to the Resi texting number. Your own texts with clients stay on your phone. {unreadTotal ? `${unreadTotal} unread.` : ''}</p>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {(!open || window.innerWidth >= 900) && (
          <div style={{ ...ui.card, flex: '1 1 280px', maxWidth: 420, padding: 0, overflow: 'hidden' }}>
            {threads.length === 0 && <p style={{ ...ui.sub, padding: 16 }}>No replies yet. When a client answers an automatic text, it shows up here.</p>}
            {threads.map((t) => (
              <button key={t.id} type="button" onClick={() => setOpen(t.id)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', borderBottom: '1px solid #E0E0E0', background: t.id === open ? '#FFF7E0' : '#fff', cursor: 'pointer', minHeight: 44 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontWeight: t.unread ? 700 : 500 }}>{t.name || t.phone}</strong>
                  {t.unread ? <span style={ui.badge}>{t.unread}</span> : <span style={{ fontSize: 12, color: '#777' }}>{when(t.lastAt)}</span>}
                </div>
                <div style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.lastDirection === 'out' ? 'You: ' : ''}{t.lastText}</div>
              </button>
            ))}
          </div>
        )}
        {open && thread && (
          <div style={{ ...ui.card, flex: '2 1 360px', minWidth: 0 }}>
            <div style={{ ...ui.row, justifyContent: 'space-between' }}>
              <h2 style={{ ...ui.sectionTitle, border: 'none', margin: 0 }}>{thread.name || thread.phone}</h2>
              <div style={ui.row}>
                {thread.contactId && <button type="button" style={ui.ghost} onClick={() => navigate(`/leads/contacts?id=${thread.contactId}`)}>Open contact</button>}
                <button type="button" style={ui.ghost} onClick={() => setOpen(null)}>Back</button>
              </div>
            </div>
            <p style={{ fontSize: 13, color: '#666', margin: '4px 0 12px' }}>{thread.phone}{thread.contactId ? '' : ' (not matched to a contact yet)'}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto', marginBottom: 12 }}>
              {msgs.map((m) => (
                <div key={m.id} style={{ alignSelf: m.direction === 'out' ? 'flex-end' : 'flex-start', maxWidth: '80%', background: m.direction === 'out' ? '#1a1a1a' : '#fff', color: m.direction === 'out' ? '#fff' : '#000', border: '1px solid #ddd', borderRadius: 10, padding: '8px 12px' }}>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{m.text}</div>
                  <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>{m.direction === 'out' ? `${m.byName || 'Sent'} · ` : ''}{when(m.at)}</div>
                </div>
              ))}
            </div>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={1600} placeholder="Type your reply..." style={{ ...ui.input, minHeight: 80 }} />
            <div style={{ ...ui.row, marginTop: 8 }}>
              <button type="button" style={ui.btn} disabled={busy || !draft.trim()} onClick={send}>{busy ? 'Sending...' : 'Send reply'}</button>
              <span style={{ fontSize: 13, color: '#666' }}>Sent from the Resi texting number exactly as typed. No name is added.</span>
            </div>
            {note && <p style={ui.notice}>{note}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// Unread count for the menu badge.
export function useInboxUnread(uid, enabled) {
  const [n, setN] = useState(0);
  useEffect(() => (uid && enabled ? watchPath(`crm/inboxThreads/${uid}`, (l) => setN(l.reduce((s, t) => s + (Number(t.unread) || 0), 0))) : undefined), [uid, enabled]);
  return n;
}
